<script lang="ts">
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu";
  import { Badge } from "$lib/components/ui/badge";
  import { Mail, Download, Loader2, AlertCircle } from "lucide-svelte";
  import { authStore } from "$lib/stores";
  import type { Snippet } from "svelte";
  import { cn } from "$lib/utility";
  import { Portal } from "bits-ui";

  const {
    onBasicExport,
    onPremiumExport,
    enablePremiumExport = true,
    creditsRequired,
    creditsAvailable,
    isProcessingCSV,
    loggedOutCallback,
    category,
    disabled,
    children,
  } = $props<{
    onBasicExport: () => Promise<void>;
    onPremiumExport: () => Promise<void>;
    enablePremiumExport?: boolean;
    loggedOutCallback?: () => void;
    isProcessingCSV: boolean;
    creditsRequired: number;
    creditsAvailable: number;
    disabled: boolean;
    category?: string;
    children: Snippet;
  }>();

  let isOpen = $state(false);
  let isExporting = $state(false);
  let error = $state<string | null>(null);

  async function handleBasicExport() {
    error = null;
    isExporting = true;
    if (!$authStore.isLoggedIn) {
      isOpen = false;
    }
    try {
      await onBasicExport();
    } catch (e) {
      error = e instanceof Error ? e.message : "Failed to download CSV";
      return;
    } finally {
      isExporting = false;
      if (!error) isOpen = false;
    }
  }

  async function handlePremiumExport() {
    error = null;
    if (!$authStore.isLoggedIn) {
      isOpen = false;
    }
    try {
      isExporting = true;
      await onPremiumExport();
    } catch (e) {
      error = e instanceof Error ? e.message : "Failed to download premium CSV";
      isExporting = false;
      return;
    } finally {
      isExporting = false;
      isOpen = true;
    }
  }
</script>

<DropdownMenu.Root
  bind:open={isOpen}
  onOpenChange={(open) => {
    if (!$authStore.isLoggedIn) {
      loggedOutCallback();
    } else {
      isOpen = open;
    }
  }}
>
  <DropdownMenu.Trigger {disabled}>
    {@render children()}
  </DropdownMenu.Trigger>

  <Portal>
    <DropdownMenu.Content
      side="bottom"
      align="start"
      sideOffset={5}
      class="!w-[300px] !rounded-base border border-gray-200 bg-theme-background-primary p-2 shadow-lg dark:border-white/40 dark:bg-theme-background-tertiary"
    >
      <!-- Basic Export Option -->
      <DropdownMenu.Item
        class={cn(
          "flex w-full cursor-pointer items-center justify-between !rounded-base p-3 outline-none",
          "hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-theme-background-primary dark:focus:bg-theme-background-primary",
          isExporting ? "cursor-not-allowed opacity-50" : "",
        )}
        disabled={isExporting}
        onclick={handleBasicExport}
      >
        <div class="space-y-1">
          <h3 class="text-sm font-medium text-theme-text-primary dark:text-theme-gray-800">Basic Export</h3>
          <p class="text-xs text-theme-text-secondary dark:text-theme-gray-500">
            Standard data without email addresses
          </p>
        </div>
        <Download class="h-4 w-4 text-theme-text-secondary dark:text-theme-gray-500" />
      </DropdownMenu.Item>

      <!-- Premium Export Option -->

      {#if category === "person"}
        <DropdownMenu.Item
          class={cn(
            enablePremiumExport ? "" : "hidden",
            "relative mt-1 flex w-full cursor-pointer items-center justify-between overflow-hidden !rounded-base p-3",
            "hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-theme-background-primary dark:focus:bg-theme-background-primary",
            creditsAvailable >= creditsRequired
              ? "hover:bg-gray-50 dark:hover:bg-theme-background-primary"
              : "cursor-not-allowed opacity-50",
          )}
          disabled={creditsAvailable < creditsRequired || isExporting || isProcessingCSV}
          onclick={creditsAvailable >= creditsRequired ? handlePremiumExport : undefined}
        >
          <div class="relative z-10 space-y-1">
            <h3 class="flex items-center gap-2 text-sm font-medium text-theme-text-primary dark:text-theme-gray-800">
              Premium Export
              <Badge
                variant="default"
                class="dark:border-theme-gray-300/20 bg-black px-1.5 py-0 text-2xs text-theme-green dark:border dark:bg-theme-background-primary"
              >
                up to {creditsRequired} credits
              </Badge>
            </h3>
            <p class="flex items-center gap-1 text-xs text-theme-text-secondary dark:text-theme-gray-500">
              <Mail class="h-3 w-3" />
              Includes verified email addresses
            </p>
          </div>
          <Download class="relative z-10 h-4 w-4 text-theme-text-secondary dark:text-theme-gray-500" />
        </DropdownMenu.Item>

        {#if error}
          <div class="px-3 py-2">
            <p class="flex items-center justify-center gap-2 text-xs text-red-700 dark:text-red-400">
              <AlertCircle class="h-3 w-3" />
              {error}
            </p>
          </div>
        {/if}

        {#if isProcessingCSV}
          <div class="flex items-center gap-1 px-3 py-2 text-sm dark:text-theme-gray-600">
            <Loader2 class="h-3 w-3 animate-spin text-theme-text-primary dark:text-theme-gray-600" />
            Processing data, please wait...
          </div>
        {/if}

        <div class="space-y-1 px-3 py-2">
          <p class="flex items-center justify-start gap-1 text-xs text-theme-text-primary dark:text-theme-gray-600">
            <AlertCircle class="h-3 w-3" />
            Current balance: {creditsAvailable} credits
          </p>
          <p class="text-xs text-gray-600 dark:text-theme-gray-500">
            Number of credits required for Premium export is an estimate. You will only be charged for the new data.
          </p>
        </div>
      {/if}
    </DropdownMenu.Content>
  </Portal>
</DropdownMenu.Root>
